import { useId, type ChangeEvent } from "react";
import { Box, Skeleton, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IconCaret } from "src/components/icons-mui/caret";
import { Button } from "src/components-mui/base/button/button";
import { TablePaginationDropdownIcon } from "src/components-mui/base/table-scrollable/components/pagination/table-pagination-dropdown-icon";
import { TablePaginationNumbers } from "src/components-mui/base/table-scrollable/components/pagination/table-pagination-numbers";
import { Typography } from "src/components-mui/base/typography/typography";
import { WfNativeSelect } from "src/components-mui/base/wf-native-select/wf-native-select";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";

interface ITablePaginationProps {
    pageSizeOptions: number[];
    pageSize: number;
    itemsCount: number;
    onPageSizeChange: (index: number) => void;
    currentPageIndex: number;
    pageIndexes: number[];
    onPageSelect: (index: number) => void;
    onPreviousClick: () => void;
    onNextClick: () => void;
    canGoPreviousPage: boolean;
    canGoNextPage: boolean;
    isLoading: boolean;
    itemsLabel: string;
    hidePageNumbers?: boolean;
}

const TABLE_PAGINATION_TEXT_TEST_ID = "table-pagination-text";

export const TablePagination = ({
    pageSizeOptions,
    pageSize,
    itemsCount,
    onPageSizeChange,
    currentPageIndex,
    pageIndexes,
    onPageSelect,
    onPreviousClick,
    onNextClick,
    canGoPreviousPage,
    canGoNextPage,
    isLoading,
    itemsLabel,
    hidePageNumbers = false,
}: ITablePaginationProps) => {
    const { t } = useTranslation("table-scrollable");
    const id = useId();
    const { formatNumber } = useNumberFormatter();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const pageStartingItemNumber = (currentPageIndex + 1) * pageSize - pageSize + 1;
    const pageLastItemNumber = (currentPageIndex + 1) * pageSize;
    const isLastPage = currentPageIndex === pageIndexes.at(-1);

    const isPaginationKnown = itemsCount > 0 && pageIndexes.length > 0;
    const showSkeleton = isLoading && !isPaginationKnown;

    return (
        <Stack
            spacing={1}
            sx={{
                mt: 3,
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                sx={{
                    alignItems: "center",
                    textAlign: "center",
                    justifyContent: { xs: "center", sm: "space-between" },
                    width: "100%",
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
            >
                {showSkeleton ? (
                    <Skeleton height="24px" width="172px" sx={{ borderRadius: "4px" }} />
                ) : (
                    <Typography
                        data-test-id={TABLE_PAGINATION_TEXT_TEST_ID}
                        sx={{
                            color: "gray.400",
                            width: { xs: "100%", sm: "auto" },
                        }}
                    >
                        {t("pagination.range", {
                            0: formatNumber(pageStartingItemNumber),
                            1: formatNumber(isLastPage ? itemsCount : pageLastItemNumber),
                            2: formatNumber(itemsCount),
                            3: itemsLabel,
                        })}
                    </Typography>
                )}
                <Box
                    sx={{
                        color: "gray.400",
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    {t("pagination.show")}
                    <Box
                        component="span"
                        sx={{
                            color: "gray.800",
                            display: "inline-block",
                        }}
                    >
                        <WfNativeSelect
                            id={id}
                            onChange={(ev: ChangeEvent<HTMLSelectElement>) => onPageSizeChange(Number(ev.target.value))}
                            value={pageSize}
                            disabled={isLoading}
                            input={undefined}
                            name={`table-page-size-select-${id}`}
                            sx={{
                                marginRight: "10px",
                                "&::before": { content: "unset" },
                                "&::after": { content: "unset" },
                                "& .MuiNativeSelect-select": { borderWidth: 0 },
                                "&.Mui-focused .MuiNativeSelect-select": { boxShadow: "none" },
                            }}
                            IconComponent={TablePaginationDropdownIcon}
                            inputProps={{ sx: { paddingRight: "16px !important", paddingLeft: 1, height: "48px", boxSizing: "border-box" } }}
                        >
                            {pageSizeOptions.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </WfNativeSelect>
                    </Box>
                    {t("pagination.items-per-page", { 0: itemsLabel })}
                </Box>
            </Stack>
            <Stack
                direction="row"
                sx={{
                    justifyContent: "space-between",
                    width: "100%",
                }}
            >
                <Button
                    disabled={!canGoPreviousPage || isLoading}
                    sx={{ px: { xs: hidePageNumbers ? 2 : 0, sm: 2 } }}
                    onClick={onPreviousClick}
                    variant="outlined"
                    size="small"
                    aria-label={t("pagination.previous")}
                >
                    {!hidePageNumbers && <IconCaret sx={{ display: { xs: "inline", sm: "none" }, transform: "rotate(90deg)", fontSize: "2xl" }} />}
                    <Box component="span" sx={{ display: { xs: hidePageNumbers ? "inline" : "none", sm: "inline" } }}>
                        {t("pagination.previous")}
                    </Box>
                </Button>
                {!hidePageNumbers && (
                    <TablePaginationNumbers
                        pageIndexes={pageIndexes}
                        currentPageIndex={currentPageIndex}
                        showSkeleton={showSkeleton}
                        isDisabled={isLoading}
                        isMobile={isMobile}
                        onPageSelect={onPageSelect}
                    />
                )}
                <Button
                    disabled={!canGoNextPage || isLoading}
                    sx={{ px: { xs: hidePageNumbers ? 2 : 0, sm: 2 } }}
                    onClick={onNextClick}
                    variant="outlined"
                    size="small"
                    aria-label={t("pagination.next")}
                >
                    {!hidePageNumbers && <IconCaret sx={{ display: { xs: "inline", sm: "none" }, transform: "rotate(270deg)", fontSize: "2xl" }} />}
                    <Box component="span" sx={{ display: { xs: hidePageNumbers ? "inline" : "none", sm: "inline" } }}>
                        {t("pagination.next")}
                    </Box>
                </Button>
            </Stack>
        </Stack>
    );
};
